import React, { useState, useEffect, useCallback } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MediaViewer = ({ 
  media, 
  onClose, 
  onNext, 
  onPrevious,
  onDownload,
  canDownload = true,
  showMediaInfo = true
}) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e?.key === 'Escape') onClose();
      if (e?.key === 'ArrowRight') onNext();
      if (e?.key === 'ArrowLeft') onPrevious();
      if (e?.key === 'z' || e?.key === 'Z') setIsZoomed(prev => !prev);
      if (e?.key === 'd' || e?.key === 'D') {
        if (canDownload) handleDownloadClick();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onClose, onNext, onPrevious, canDownload]);

  // Touch gesture handling for mobile swipe
  const handleTouchStart = useCallback((e) => {
    setTouchEnd(null);
    setTouchStart(e?.targetTouches?.[0]?.clientX);
  }, []);

  const handleTouchMove = useCallback((e) => {
    setTouchEnd(e?.targetTouches?.[0]?.clientX);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      onNext();
    }
    if (isRightSwipe) {
      onPrevious();
    }
  }, [touchStart, touchEnd, onNext, onPrevious]);

  // Handle download with feedback
  const handleDownloadClick = useCallback(async () => {
    setDownloading(true);
    try {
      await onDownload(media);
    } finally {
      setTimeout(() => setDownloading(false), 1000);
    }
  }, [media, onDownload]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  return (
    <div 
      className="fixed inset-0 z-[2000] bg-black/95 flex flex-col"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="flex items-center justify-between p-4 md:p-6 border-b border-border/20">
        <div className="flex-1 min-w-0">
          {showMediaInfo && (
            <>
              <h2 className="text-lg md:text-xl font-semibold text-white mb-1 truncate">
                {media?.title}
              </h2>
              <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-gray-400">
                <span className="data-text">{formatFileSize(media?.size)}</span>
                {media?.type === 'video' && (
                  <>
                    <span>•</span>
                    <span className="data-text">{formatDuration(media?.duration)}</span>
                  </>
                )}
                <span>•</span>
                <span>{new Date(media.uploadDate)?.toLocaleDateString()}</span>
              </div>
            </>
          )}
          {!showMediaInfo && (
            <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-gray-400">
              <span className="data-text">{formatFileSize(media?.size)}</span>
              {media?.type === 'video' && (
                <>
                  <span>•</span>
                  <span className="data-text">{formatDuration(media?.duration)}</span>
                </>
              )}
              <span>•</span>
              <span>{new Date(media.uploadDate)?.toLocaleDateString()}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 ml-4">
          {media?.type === 'image' && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsZoomed(!isZoomed)}
              className="text-white hover:bg-white/10"
              title="Toggle zoom (Z)"
            >
              <Icon name={isZoomed ? 'ZoomOut' : 'ZoomIn'} size={20} />
            </Button>
          )}
          
          {canDownload && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDownloadClick}
              disabled={downloading}
              className="text-white hover:bg-white/10"
              title="Download (D)"
            >
              {downloading ? (
                <Icon name="Loader2" size={20} className="animate-spin" />
              ) : (
                <Icon name="Download" size={20} />
              )}
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-white hover:bg-white/10"
            title="Close (Esc)"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-4 md:p-8 relative">
        <button
          onClick={onPrevious}
          className="absolute left-2 md:left-4 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-250 z-10"
          title="Previous (←)"
        >
          <Icon name="ChevronLeft" size={24} color="white" />
        </button>

        <div className="max-w-full max-h-full flex items-center justify-center">
          {media?.type === 'image' ? (
            <div className={`transition-transform duration-250 ${isZoomed ? 'scale-150 cursor-zoom-out' : 'scale-100 cursor-zoom-in'}`}
              onClick={() => setIsZoomed(!isZoomed)}
            >
              <Image
                src={media?.url}
                alt={media?.alt}
                className="max-w-full max-h-[calc(100vh-200px)] object-contain"
              />
            </div>
          ) : (
            <div className="w-full max-w-5xl aspect-video bg-black rounded-lg overflow-hidden">
              <video
                src={media?.url}
                controls
                autoPlay
                className="w-full h-full"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
              >
                Your browser does not support video playback.
              </video>
            </div>
          )}
        </div>

        <button
          onClick={onNext}
          className="absolute right-2 md:right-4 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-250 z-10"
          title="Next (→)"
        >
          <Icon name="ChevronRight" size={24} color="white" />
        </button>
      </div>
      {showMediaInfo && media?.description && (
        <div className="p-4 md:p-6 border-t border-border/20 bg-black/50">
          <p className="text-sm md:text-base text-gray-300 max-w-4xl mx-auto">
            {media?.description}
          </p>
        </div>
      )}
    </div>
  );
};

export default MediaViewer;